import { useState, useEffect } from 'react';
import { publicHomeService, Testimonial, Caregiver, StatsData } from '../services/publicHomeService';

interface UsePublicHomeReturn {
  testimonials: Testimonial[];
  featuredCaregivers: Caregiver[];
  stats: StatsData | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export const usePublicHome = (): UsePublicHomeReturn => {
  const [testimonials, setTestimonials] = useState<Testimonial[]>([]);
  const [featuredCaregivers, setFeaturedCaregivers] = useState<Caregiver[]>([]);
  const [stats, setStats] = useState<StatsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch all data in parallel for better performance
      const [testimonialsData, caregiversData, statsData] = await Promise.all([
        publicHomeService.getTestimonials(),
        publicHomeService.getFeaturedCaregivers(),
        publicHomeService.getStats(),
      ]);

      setTestimonials(testimonialsData);
      setFeaturedCaregivers(caregiversData);
      setStats(statsData);
    } catch (err) {
      console.error('Error fetching public home data:', err);
      setError('Failed to load homepage data. Please try again later.');
      
      // Set fallback data on error
      setTestimonials([]);
      setFeaturedCaregivers([]);
      setStats(null);
    } finally {
      setLoading(false);
    }
  };

  const refetch = async () => {
    await fetchData();
  };

  useEffect(() => {
    fetchData();
  }, []);

  return {
    testimonials,
    featuredCaregivers,
    stats,
    loading,
    error,
    refetch,
  };
};
